/**
 * Loads an image with a skeleton loader
 * @param {string} url - The image URL to load
 * @param {HTMLImageElement | string} element - The image element
 * @param {HTMLElement | string} wrapper - The wrapper element that contains the image
 * @param {Object} [options] - Optional configuration options (width, height, classes, fallbackUrl)
 * @returns {Promise} - Promise that resolves when the image loads
 */
export function loadImage(url, element, wrapper, options = {width: 100, height: 100}) {
    if (!wrapper && element) {
        wrapper = element.parentElement;
    }

    if (typeof element == 'string') {
        element = document.getElementById(element);
    }

    if (typeof wrapper == 'string') {
        wrapper = document.getElementById(wrapper);
    }

    if (!element || !wrapper) {
        throw new Error('Invalid parameters provided. Element, and wrapper are required.');
    }

    if (!url && !options.fallbackUrl) {
        throw new Error('Invalid URL provided. URL or fallbackUrl is required.');
    }

    if (!url) {
        url = options.fallbackUrl;
    }

    return new Promise((resolve, reject) => {
        // Store original dimensions for reference (if available)
        const originalWidth = element.offsetWidth || element.getAttribute('width') || wrapper.offsetWidth;
        const originalHeight = element.offsetHeight || element.getAttribute('height') || wrapper.offsetHeight;
        
        // Create and add the skeleton loader
        const skeleton = document.createElement('div');
        skeleton.classList.add('skeleton');
        
        // Set minimum dimensions if originals are too small
        if (options.classes) {
            if (!Array.isArray(options.classes)) {
                options.classes = [options.classes];
            }
            options.classes.forEach(className => skeleton.classList.add(className));
        } else {

            if (options.width) {
                skeleton.style.width = `${options.width}px`;
            } else {
                skeleton.style.width = `${originalWidth}px`;
            }

            if (options.height) {
                skeleton.style.height = `${options.height}px`;
            } else {
                skeleton.style.height = `${originalHeight}px`;
            }
        }
        
        // Hide the image before starting to load
        element.classList.add('hidden');
        
        // Add the skeleton to the wrapper
        wrapper.appendChild(skeleton);
        
        // Set up the onload handler before setting the src
        element.onload = () => {
            // Remove the skeleton with a slight delay for a smoother transition
            setTimeout(() => {
                // Ensure the image is properly loaded before showing it
                element.classList.remove('hidden');
                if (skeleton && skeleton.parentNode) {
                    skeleton.remove();
                }
                resolve(element);
            }, 50);
        };
        
        // Add error handler
        element.onerror = (error) => {
            console.error('Failed to load image:', url, error);
            
            // Remove the skeleton
            if (skeleton && skeleton.parentNode) {
                skeleton.remove();
            }
            
            // Show a placeholder or error state
            element.src = '/img/account-placeholder.png';
            element.classList.remove('hidden');
            
            resolve(error);
        };
        
        // Set the image source last to trigger loading
        element.src = url;
    });
}

export function simpleImageLoader(url, element, options) {
    return loadImage(url, element, null, options);
}